import { useThemeUserPreference } from '@unifyapps/carbon/no-code/hooks/useThemeUserPreference';
import { getShouldEvaluateResult } from '@unifyapps/carbon/no-code/utils/getShouldEvaluateResult';
import { evaluateThemeId } from '@unifyapps/carbon/theme/utils/evaluateThemeId';
import { flattenThemeEntities } from '@unifyapps/carbon/theme/utils/transformTheme';
import { useEffect, useMemo } from 'react';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import { ComputeContextKeys } from '../../../../hooks/computeContext/const';
import { useReactiveAppComputeContext } from '../../../../hooks/computeContext/useGetAppComputeContext';
import { useInterfaceThemes } from '../../../../hooks/useInterfaceThemes';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';

/**
 * Fetches interface themes via useInterfaceThemes, flattens them, and syncs availableThemes
 * and activeTheme to GlobalAppStateStore. Active theme is set based on:
 * - in builder use interface theme id from cookie/search param.
 * - in runner use priority order user preference → conditional themes (from context) → interface defaultThemeId
 * Notifies dependency graph when themes change so compute context stays in sync.
 */
export function useSetAvailableThemesAndActiveTheme() {
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const interfaceThemeId = useGlobalAppStateStore().hooks.useInterfaceThemeId();
  const activeTheme = useGlobalAppStateStore().hooks.useActiveTheme();
  const availableThemes = useGlobalAppStateStore().hooks.useAvailableThemes();
  const { setAvailableThemes, setActiveTheme } = useGlobalAppStateStore().hooks.useActions();
  const {
    actions: { notifyDependencyChange },
  } = useAppDependencyGraphProvider();
  const { context } = useReactiveAppComputeContext();
  const { themes } = useInterfaceThemes(interfaceRecord);
  const { userPreferenceThemeId, isUserPreferenceThemeIdLoading } = useThemeUserPreference(
    interfaceRecord?.id,
  );

  const defaultThemeId = interfaceRecord?.properties.theme?.defaultThemeId;

  const { evaluatedTheme, flattenedThemes } = useMemo(() => {
    const flattenedThemes = flattenThemeEntities(themes);
    const evaluatedThemeId = evaluateThemeId({
      defaultThemeId,
      context,
      preferredThemeId: interfaceThemeId ?? userPreferenceThemeId,
      shouldEvaluateResult: getShouldEvaluateResult(interfaceRecord?.properties.flags),
    });
    const evaluatedTheme = flattenedThemes.find((theme) => theme.id === evaluatedThemeId);

    return { evaluatedTheme, flattenedThemes };
  }, [
    defaultThemeId,
    context,
    interfaceThemeId,
    userPreferenceThemeId,
    themes,
    interfaceRecord?.properties.flags,
  ]);

  useEffect(() => {
    if (availableThemes?.length !== flattenedThemes.length) {
      setAvailableThemes(flattenedThemes);
    }
    if (isUserPreferenceThemeIdLoading) return;
    // set active theme only once on first load. when user switches theme later,
    // useSetTheme updates activeTheme directly, so we skip it here.
    if (!activeTheme) {
      setActiveTheme(evaluatedTheme);
      notifyDependencyChange({
        id: ComputeContextKeys.THEME,
      });
    }
  }, [
    flattenedThemes,
    evaluatedTheme,
    availableThemes,
    setAvailableThemes,
    setActiveTheme,
    notifyDependencyChange,
    activeTheme,
    isUserPreferenceThemeIdLoading,
  ]);
}
